import { AppstoreOutlined, DeleteOutlined, EditOutlined } from "@ant-design/icons";
import { getClientEnv } from "@shared/env";
import { Button, Image, Modal, Popconfirm } from "antd";
import { useNavigate } from "react-router-dom";
import type { AppRole } from "@/shared/config/navigation";
import { canManageObjectStructure } from "@/shared/config/permissions";
import type { Building } from "../types/objects.types";
import { getUploadedAssetUrl } from "../utils/uploaded-asset-url";

const env = getClientEnv();

interface BuildingCardProps {
	building: Building;
	isDeleting?: boolean;
	onDelete: (building: Building) => void;
	onEdit: (building: Building) => void;
	onDeleteImage?: (building: Building) => void;
	userRole: AppRole;
}

export function BuildingCard({
	building,
	isDeleting = false,
	onDelete,
	onEdit,
	onDeleteImage,
	userRole,
}: BuildingCardProps) {
	const navigate = useNavigate();
	const canManageBuilding = canManageObjectStructure(userRole);
	const soldPercent =
		building.totalCount > 0 ? Math.round((building.soldCount / building.totalCount) * 100) : 0;
	const imageUrl = getUploadedAssetUrl(env.VITE_API_URL, building.image);

	return (
		<div className="crm-card">
			<div
				className="crm-imgph"
				style={{
					height: 120,
					overflow: "hidden",
					padding: 0,
					borderBottom: "1px solid var(--crm-border)",
					borderRadius: "10px 10px 0 0",
					position: "relative",
				}}
			>
				{imageUrl ? (
					<div className="crm-image-preview-wrapper" style={{ width: "100%", height: "100%" }}>
						<Image
							alt={building.name}
							className="crm-building-card-image"
							src={imageUrl}
							style={{ width: "100%", height: "120px", objectFit: "cover" }}
							wrapperStyle={{ width: "100%", height: "100%" }}
						/>
						{onDeleteImage && (
							<Button
								type="text"
								icon={<DeleteOutlined style={{ color: "var(--crm-red)" }} />}
								onClick={(e) => {
									e.stopPropagation();
									Modal.confirm({
										title: "Rasmni o'chirish",
										content: "Haqiqatan ham bino fasad rasmini o'chirib tashlamoqchimisiz?",
										okText: "O'chirish",
										okType: "danger",
										cancelText: "Bekor qilish",
										onOk: () => {
											onDeleteImage(building);
										},
									});
								}}
								className="crm-image-delete-btn"
							/>
						)}
					</div>
				) : (
					<span>{building.code || building.name}.jpg - fasad foto</span>
				)}
			</div>
			<div className="crm-card-body">
				<div className="crm-org-name-cell">
					<span className={`crm-mini-avatar ${building.tone || "blue"}`}>{building.initials}</span>
					<div className="grow">
						<strong style={{ fontSize: "15px" }}>{building.name}</strong>
						<span style={{ fontSize: "11px", color: "var(--crm-ink-3)" }}>
							kod: {building.code}
						</span>
					</div>
					<div className="crm-status-badge green">
						<span /> {building.isActive === false ? "Nofaol" : "Faol"}
					</div>
				</div>

				<div className="crm-inline-fields crm-mt" style={{ gap: "8px" }}>
					<div>
						<div style={{ fontSize: "11px", color: "var(--crm-ink-3)" }}>Bloklar</div>
						<strong style={{ fontSize: "14px" }} className="num">
							{building.blocksCount}
						</strong>
					</div>
					<div>
						<div style={{ fontSize: "11px", color: "var(--crm-ink-3)" }}>Sotilgan</div>
						<strong style={{ fontSize: "14px" }} className="num">
							{building.soldCount}
						</strong>
					</div>
					<div>
						<div style={{ fontSize: "11px", color: "var(--crm-ink-3)" }}>Bo'sh</div>
						<strong style={{ fontSize: "14px", color: "var(--crm-green)" }} className="num">
							{building.availableCount}
						</strong>
					</div>
				</div>

				<div className="crm-mt">
					<div className="crm-prog">
						<div className="crm-prog-fill green" style={{ width: `${soldPercent}%` }} />
					</div>
					<div
						style={{
							display: "flex",
							justifyContent: "space-between",
							fontSize: "11px",
							marginTop: "4px",
							color: "var(--crm-ink-2)",
						}}
					>
						<span>Sotuv progressi</span>
						<span>{soldPercent}%</span>
					</div>
				</div>

				<div className="crm-building-actions crm-mt">
					<Button style={{ flex: 1 }} onClick={() => navigate(`/objects/${building.id}/blocks`)}>
						Bloklar
					</Button>
					<Button
						icon={<AppstoreOutlined />}
						onClick={() => navigate(`/rooms?orgId=${building.orgId}&buildingId=${building.id}`)}
					/>
					{canManageBuilding ? (
						<>
							<Button icon={<EditOutlined />} onClick={() => onEdit(building)} />
							<Popconfirm
								cancelText="Yo'q"
								okButtonProps={{ danger: true, loading: isDeleting }}
								okText="Ha"
								onConfirm={() => onDelete(building)}
								title="Binoni o'chirish?"
							>
								<Button danger icon={<DeleteOutlined />} loading={isDeleting} />
							</Popconfirm>
						</>
					) : null}
				</div>
			</div>
		</div>
	);
}
